export const DEFAULT_CHANGE_DETECTION_EXCLUDE = ['message_id', 'sequence', 'occurred_at', 'source']

export type ChangeDetectionConfig = {
  enabled?: boolean
  identityPaths?: string[]
  compare?: 'mapped-body' | string
  excludePaths?: string[]
  persistSequence?: boolean
}

const IDENTITY_HINTS = ['payload.employee_ref', 'payload.locationCode', 'payload.vehicle_name']
const ENVELOPE_IDENTITY_SKIP = new Set([
  'message_id',
  'sequence',
  'occurred_at',
  'schema_version',
  'entity',
  'source',
  'source.system',
  'source.record_ref',
  'source.tenant_id',
])

export function suggestIdentityPaths(targetPaths: string[]): string[] {
  const set = new Set((targetPaths || []).filter(Boolean))
  const picks: string[] = []
  for (const path of IDENTITY_HINTS) {
    if (set.has(path)) picks.push(path)
  }
  if (picks.includes('payload.vehicle_name') && set.has('payload.occurred_at')) {
    picks.push('payload.occurred_at')
  }
  if (!picks.length && set.has('payload.name')) {
    picks.push('payload.name')
  }
  if (!picks.length) {
    const first = (targetPaths || []).find(
      path => path && !ENVELOPE_IDENTITY_SKIP.has(path) && !path.startsWith('source.')
    )
    if (first) picks.push(first)
  }
  return picks
}

export function getByPath(obj: any, path: string): any {
  if (!path || obj == null) return null
  const keys = path.split('.')
  let value = obj
  for (const key of keys) {
    if (value == null) return null
    if (/\[\d+\]/.test(key)) {
      const [propertyName, indexStr] = key.split(/\[|\]/).filter(Boolean)
      const index = parseInt(indexStr, 10)
      if (propertyName) value = value[propertyName]
      if (Array.isArray(value) && index >= 0 && index < value.length) {
        value = value[index]
      } else {
        return null
      }
    } else {
      value = value[key]
    }
  }
  return value
}

function identityPart(value: any): string {
  if (value == null || value === '') return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function identityKey(record: any, identityPaths: string[]): string {
  if (!identityPaths?.length || !record) return ''
  const parts = identityPaths.map(path => identityPart(getByPath(record, path)))
  if (parts.some(part => !part)) return ''
  return parts.join('|')
}

export function previewChangeDetectionStats(
  sourceRecords: any[],
  mappings: Array<{ sourcePath?: string; targetPath?: string }>,
  identityPaths: string[]
) {
  const paths = (identityPaths || []).filter(Boolean)
  const identities: string[] = []
  let missingIdentity = 0

  for (const record of sourceRecords || []) {
    const parts = paths.map(path => {
      const edge = mappings.find(mapping => mapping.targetPath === path)
      const value = edge?.sourcePath ? getByPath(record, edge.sourcePath) : getByPath(record, path)
      return identityPart(value)
    })
    if (!paths.length || parts.some(part => !part)) {
      missingIdentity += 1
      continue
    }
    identities.push(parts.join('|'))
  }

  const unique = new Set(identities)
  return {
    total: sourceRecords?.length || 0,
    uniqueIdentities: unique.size,
    inBatchDuplicates: Math.max(0, identities.length - unique.size),
    missingIdentity,
  }
}
